import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { updateIsUsingHydration } from './update-is-using-hydration';
import { currentUrl } from './url';
import { type Theme } from '@glitz/core';

export type PropType = {
  tagName?: string;
  skipHydration?: boolean;
  renderOnUpdate?: boolean;
  rootMargin?: string;
};

let ChildlessProviders: React.ComponentType<React.PropsWithChildren<any>>;
let getProps: () => { theme: Theme } & Record<string, any>;
export function registerChildlessProviders(
  Component: React.ComponentType<React.PropsWithChildren<any>>,
  props: () => { theme: Theme } & Record<string, any>,
) {
  getProps = props;
  ChildlessProviders = Component;
}

/*
ProgressiveHydration wraps the components in an additional element it may have breaking effect on some styles. 
Example, the progressive hydrated component is a flex-child and uses align-self: flex-end.  
That style will no longer work since it's wrapped in a outer element.
*/
const ProgressiveHydration = React.memo(
  React.forwardRef(function ProgressiveHydration(
    { children, renderOnUpdate, skipHydration, tagName = 'div', rootMargin }: React.PropsWithChildren<PropType>,
    ref: React.Ref<HTMLElement>,
  ) {
    const elementRefRef = React.useRef<HTMLElement>(null);
    React.useImperativeHandle<HTMLElement | null, HTMLElement | null>(ref, () => elementRefRef.current);

    const contextOptions = useHydrationOptions();
    const hasHydratedRefRef = React.useRef(false);
    const renderOnUpdateRef = React.useRef(renderOnUpdate);
    if (contextOptions?.renderInsteadOfHydrate || renderOnUpdate) {
      renderOnUpdateRef.current = true;
    }

    const initialHydrationChildrenRef = React.useRef<React.ReactNode>(children);
    initialHydrationChildrenRef.current = children;

    const willHydrate =
      __BROWSER__ &&
      updateIsUsingHydration() &&
      !renderOnUpdateRef.current &&
      !hasHydratedRefRef.current &&
      currentUrl().searchParams.get('progressive-hydration') !== 'off';

    React.useEffect(() => {
      const element = elementRefRef.current?.firstElementChild;
      if (element && willHydrate && !skipHydration) {
        new IntersectionObserver(
          async ([entry], obs) => {
            if (!entry.isIntersecting) {
              return;
            }

            if (elementRefRef.current) {
              if (!hasHydratedRefRef.current) {
                const props = getProps();
                ReactDOMClient.hydrateRoot(
                  elementRefRef.current,
                  <ChildlessProviders suppressHydrationWarning {...props}>
                    {initialHydrationChildrenRef.current}
                  </ChildlessProviders>,
                );
                hasHydratedRefRef.current = true;
              }
              obs.unobserve(entry.target);
            }
          },
          rootMargin ? { rootMargin } : undefined,
        ).observe(element);
      }
    }, [rootMargin, skipHydration, willHydrate]);

    const style = { display: 'contents' };

    return willHydrate
      ? React.createElement(tagName, {
          suppressHydrationWarning: true,
          dangerouslySetInnerHTML: { __html: '' },
          style,
          ref: elementRefRef,
        })
      : React.createElement(tagName, { ref: elementRefRef, style }, children);
  }),
);

// ProgressiveHydration break the react context because render in a different tree.
export function withProgressiveHydration<TProps>(
  Component: React.ComponentType<React.PropsWithChildren<TProps>>,
  hydrationOptions: PropType = {},
) {
  const WithProgressiveHydration = (props: React.PropsWithChildren<TProps>) => (
    <ProgressiveHydration {...hydrationOptions}>
      <Component {...props} />
    </ProgressiveHydration>
  );
  return WithProgressiveHydration;
}

export default ProgressiveHydration;

type HydrationOptionsType = {
  renderInsteadOfHydrate: boolean;
};

const HydrationOptionsContext = React.createContext<HydrationOptionsType | undefined>(undefined);

function useHydrationOptions() {
  return React.useContext(HydrationOptionsContext);
}

export const HydrationOptionsProvider = ({ children, ...value }: React.PropsWithChildren<HydrationOptionsType>) => {
  return <HydrationOptionsContext.Provider value={value}>{children}</HydrationOptionsContext.Provider>;
};
